<template>
  <div
    class="phone-input"
    :class="{
      'phone-input--has-text': !!number,
      'phone-input--invalid': !valid,
    }"
  >
    <div class="phone-input__wrapper">
      <vue-tel-input
        :input-id="name"
        :name="name"
        v-bind="$attrs"
        :value="number"
        mode="international"
        placeholder=""
        @input="onInput"
        @validate="onValidate"
        @blur="$emit('blur', $event)"
        @country-changed="onCountryChanged"
      ></vue-tel-input>
      <span class="sf-input__bar"></span>
      <label class="phone-input__label" :for="name">
        {{ label }}
      </label>
    </div>
    <div class="phone-input__error-message">
      <transition name="fade">
        <!-- @slot Custom error message of form input -->
        <slot v-if="!valid" name="error-message" v-bind="{ errorMessage }">
          <span>{{ errorMessage }}</span></slot
        >
      </transition>
    </div>
  </div>
</template>
<script>
import { focus } from "@lib/utility/directives/focus-directive.js";
import { VueTelInput } from "vue-tel-input";
export default {
  name: "PhoneInput",
  directives: {
    focus,
  },
  components: { VueTelInput },
  inheritAttrs: false,
  props: {
    value: {
      type: [Object],
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: null,
    },
    /**
     * Validate value of form input
     */
    valid: {
      type: Boolean,
      default: true,
    },
    /**
     * Error message value of form input. It will be appeared if `valid` is `true`.
     */
    errorMessage: {
      type: String,
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
      description: "Native input required attribute",
    },
    disabled: {
      type: Boolean,
      default: false,
      description: "Native input disabled attribute",
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler: function (val) {
        this.number = val.number;
      },
    },
  },
  data() {
    return {
      dialCode: "",
      number: "",
    };
  },
  created() {
    this.number = this.value.number || "";
  },
  methods: {
    onInput(e) {
      this.number = e;
      let number = e;
      if (e && e.startsWith(this.dialCode)) {
        number = e
          .substring(this.dialCode.length, e.length + 1)
          .replace(/\D/g, "");
      }
      let phoneObj = { dialCode: this.dialCode, number };
      this.$emit("input", phoneObj);
    },
    onCountryChanged(e) {
      this.dialCode = `+${e.dialCode}`;
    },
    onValidate(e) {
      this.isValid = e.valid;
      this.$emit("validate", e.isValid);
    },
  },
};
</script>
<style lang="scss">
@import "~@lib/styles/helpers";
.phone-input {
  position: relative;
  box-sizing: border-box;
  width: var(--input-width);
  height: var(--input-height);
  &__label {
    position: absolute;
    display: var(--input-label-display);
    top: var(--input-label-top, 50%);
    left: var(--input-label-left, 64px);
    color: var(--input-label-color, inherit);
    transform: var(
      --input-label-transform,
      translate3d(0, calc(var(--input-label-top, 50%) * -1), 0)
    );
    transition: var(
      --input-label-transition,
      top 150ms linear,
      left 150ms linear,
      font-size 150ms linear
    );
    &::after {
      content: var(--input-label-required);
      color: var(--input-label-color, var(--c-primary));
    }
    @include font(
      --input-label-font,
      var(--font-normal),
      var(--font-lg),
      1,
      var(--font-family-secondary)
    );
  }
  &__bar {
    position: relative;
    display: var(--input-bar-display, block);
    &::before,
    &::after {
      position: absolute;
      bottom: 0;
      width: var(--input-bar-width, 0);
      height: 1px;
      background: var(--input-bar-background, var(--c-primary));
      transition: width 150ms linear;
      content: "";
    }
    &::before {
      left: 50%;
    }
    &::after {
      right: 50%;
    }
  }
  &__error-message {
    color: var(--input-error-message-color, var(--c-danger));
    height: calc(var(--font-xs) * 1.2);
    @include font(
      --input-error-message-font,
      var(--font-medium),
      var(--font-xs),
      1.2,
      var(--font-family-secondary)
    );
  }
  &__wrapper,
  input {
    width: 100%;
    height: 100%;
  }
  &__wrapper {
    position: relative;
    margin: var(--input-margin, 0 0 var(--spacer-xs) 0);
  }
  input {
    display: block;
    box-sizing: border-box;
    padding: var(--input-padding, var(--spacer-sm) 0 var(--spacer-xs) 0);
    @include border(--input-border, 0 0 1px 0, solid, var(--c-light));
    background: var(--input-background);
    color: var(--input-color, var(--c-text));
    @include font(
      --input-font,
      var(--font-normal),
      var(--font-lg),
      1,
      var(--font-family-secondary)
    );
    text-align: var(--input-text-align);
    &[type="number"] {
      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      -moz-appearance: textfield;
    }
    &[type="search"] {
      -webkit-appearance: none;
    }
  }
  &--invalid {
    --input-border-color: var(--c-danger);
    input {
      &:focus {
        & ~ * {
          --input-bar-background: var(--c-danger);
          --input-label-color: var(--c-danger);
        }
      }
    }
  }
  &--has-text {
    --input-label-top: 0;
    --input-label-font-size: var(--font-2xs);
    --input-label-left: 0;
  }
  &--filled {
    --input-background: var(--c-light);
    --input-font-size: var(--font-xs);
    --input-padding: var(--spacer-xs);
    --input-label-left: var(--spacer-xs);
    --input-label-font-size: var(--font-xs);
    --input-label-color: var(--c-secondary-variant);
    --input-label-transition: color 150ms ease;
    &.phone-input--has-text {
      --input-label-display: none;
    }
    input {
      &:focus {
        & ~ * {
          --input-bar-width: unset;
          --input-label-top: 50%;
          --input-label-font-size: var(--font-xs);
          --input-label-color: var(--c-text);
        }
      }
    }
  }

  .vue-tel-input {
    border: none;

    &:focus-within {
      & ~ * {
        --icon-color: var(--c-primary);
        --input-password-icon-background: var(--c-primary);
        --input-label-top: 0;
        --input-label-color: var(--c-primary);
        --input-label-font-size: var(--font-2xs);
        --input-bar-width: 50%;
        --input-error-message-color: var(--c-primary);
        --input-label-left: 0;
      }
      outline: none;
      box-shadow: none;
      border: none;
    }
  }

  input {
    padding-left: 10px;
  }

  .vti__dropdown {
    padding-bottom: 0;
  }
}
</style>
